import { z } from 'zod';
import { PRICE_VIEWS, PROPOSAL_DETAILS, TIMING_MODES } from './proposal.js';
// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------
/** What a line names. A service is not here: a service is a *service*. */
export const QUOTE_LINE_KINDS = ['item', 'labor', 'deployment', 'equipment', 'sub'];
/**
 * What each kind is called on screen.
 *
 * Labels only — the enum values are what the database holds and they are not
 * changing here (`docs/catalog-and-packages.md` §9, PR K). "Material" and
 * "Fee" are what the trade calls these two, and "Item" and "Deployment" are
 * what the schema happened to call them; a quoter should read the first pair.
 */
export const QUOTE_LINE_KIND_LABELS = {
    item: 'Material',
    labor: 'Labor',
    deployment: 'Fee',
    equipment: 'Equipment',
    sub: 'Sub',
};
/**
 * Items only, and only two answers.
 *
 * `deploy_return` is material that goes out and comes back — planters, mats,
 * salt bins. It is the flag that makes a pickup visit necessary, so the
 * pre-send checks look for one and offer to add it.
 */
export const LINE_HANDLING = ['consume', 'deploy_return'];
export const LINE_HANDLING_LABELS = {
    consume: 'Consume',
    deploy_return: 'Deploy & pick up later',
};
/** How often a visit happens. */
export const VISIT_SCHEDULES = ['one_time', 'weekly', 'biweekly', 'monthly', 'custom'];
export const VISIT_SCHEDULE_LABELS = {
    one_time: 'One-time',
    weekly: 'Weekly',
    biweekly: 'Bi-weekly',
    monthly: 'Monthly',
    custom: 'Custom',
};
/**
 * What kind of trip a visit is.
 *
 * `work` is the ordinary one and what everything made before packages is.
 * `inspect` looks, measures or diagnoses and does no work — the mechanic's
 * first trip, the roofer's walk-through (migration 0121). The other four are
 * the shape of a rental: `place` puts the units on site,
 * `service` is the recurring trip while they are there, `swap` exchanges one
 * for another, `pickup` takes them away. The crew board and the visit read
 * this, so a driver dropping a dumpster is not told to mow.
 */
export const VISIT_ROLES = ['work', 'inspect', 'place', 'service', 'swap', 'pickup'];
export const VISIT_ROLE_LABELS = {
    work: 'Work',
    inspect: 'Inspect',
    place: 'Drop off',
    service: 'Service',
    swap: 'Swap',
    pickup: 'Pick up',
};
/**
 * What decides when a visit happens, or when it stops.
 *
 * `date` is the calendar and is nearly everything. `term_end` is a recurring
 * visit that runs until the units come back — it has a start and no end, and
 * the number of trips is genuinely unknown rather than defaulted to one.
 * `on_call` is the trip that happens when the customer rings: a pickup on an
 * open-ended rental has no date and never will until somebody asks for it.
 */
export const VISIT_TRIGGERS = ['date', 'term_end', 'on_call'];
export const VISIT_TRIGGER_LABELS = {
    date: 'On a date',
    term_end: 'While on rent',
    on_call: 'On call',
};
/** Roughly how many times a schedule fires in a week. `custom` is unknowable. */
export const SCHEDULE_PER_WEEK = {
    one_time: null,
    weekly: 1,
    biweekly: 0.5,
    monthly: 12 / 52,
    custom: null,
};
/**
 * The five ways a company bills for work (migration 0074).
 *
 * The six that came before were a guess made before anybody had priced real
 * work with them. `monthly_even`, `monthly_actual` and `rental_periods` were
 * one kind — a period — with the period hard-coded to a month and the basis
 * hard-coded into the name; `per_visit` is `completion` said in the customer's
 * words; and time and materials, which a third of field work is sold on, was
 * missing entirely.
 */
export const BILLING_PLAN_KINDS = [
    'completion', 'period', 'milestones', 'deposit_balance', 'time_materials',
];
export const BILLING_PLAN_KIND_LABELS = {
    completion: 'Completion',
    period: 'Period',
    milestones: 'Milestones',
    deposit_balance: 'Deposit',
    time_materials: 'Time & materials',
};
/** The one-line description under a plan's name, when it has nothing else. */
export const BILLING_PLAN_KIND_BLURBS = {
    completion: 'The whole visit, each time one is completed',
    period: 'Every so often — spread evenly, or what the period actually held',
    milestones: 'A list you write, each with its own trigger',
    deposit_balance: 'A percentage on approval, the rest on completion',
    time_materials: 'Quoted as an estimate, invoiced from the hours and materials',
};
/**
 * How long a period is: `period_every` of these.
 *
 * Two numbers rather than an enum of named frequencies, so fortnightly and
 * every-ten-days are the same control as weekly instead of two more members
 * somebody has to add.
 */
export const PERIOD_UNITS = ['day', 'week', 'month'];
export const PERIOD_UNIT_LABELS = {
    day: 'days',
    week: 'weeks',
    month: 'months',
};
/**
 * How a period plan divides its money.
 *
 * `even` is the contract answer — the same amount lands every period whether
 * or not a visit happened, which is what makes a season affordable to both
 * sides. `actual` bills what the period actually held, which is what a rental
 * and a skipped-for-rain round both need.
 */
export const PERIOD_BASES = ['even', 'actual'];
export const PERIOD_BASIS_LABELS = {
    even: 'Spread evenly',
    actual: 'Actuals',
};
/** When a time-and-materials plan gathers its actuals into an invoice. */
export const TM_TRIGGERS = ['visit_completion', 'period', 'job_completion'];
export const TM_TRIGGER_LABELS = {
    visit_completion: 'Each visit',
    period: 'Every period',
    job_completion: 'At the end',
};
export const MILESTONE_TRIGGERS = [
    'on_approval', 'on_date', 'visit_completed', 'progress', 'job_completed',
];
export const MILESTONE_TRIGGER_LABELS = {
    on_approval: 'On approval',
    on_date: 'On a date',
    visit_completed: 'On visit completion',
    progress: 'At % complete',
    job_completed: 'When the job is done',
};
/*
 * The controls each kind was missing (migration 0123,
 * docs/visits-billing-and-proposal.md §7.2). Every default is what the
 * planner did before the column existed, so a plan nobody touches invoices
 * exactly as it did.
 */
/** Whose terms an invoice is due on: the customer's account, or this plan's own days. */
export const TERMS_MODES = ['customer', 'days'];
/** A completion plan: an invoice per visit, or one when the last visit is done. */
export const COMPLETION_MODES = ['each', 'last'];
/** A monthly period plan: whole periods, or a part month billed by its days. */
export const PART_PERIODS = ['full', 'prorate'];
/** A deposit's balance: when the last visit is done, or across the visits. */
export const REST_MODES = ['last', 'spread'];
/** What a quote with no account reads as its customer's terms (§7.2). */
export const DEFAULT_CUSTOMER_TERMS_DAYS = 30;
export const INVOICE_ROLES = [
    'deposit', 'balance', 'spread', 'stage', 'once', 'visit', 'period', 'prorate', 'tm_job',
];
// ---------------------------------------------------------------------------
// What the client may send
// ---------------------------------------------------------------------------
const money = z.number().finite().nonnegative();
const qty = z.number().finite().positive();
export const visitInputSchema = z.object({
    name: z.string().trim().max(160).optional(),
    qty: z.number().int().positive().max(999).optional(),
    schedule: z.enum(VISIT_SCHEDULES).optional(),
    /** Kind of visit (§4.5): refused when the company has it switched off, unless the visit has it already. */
    role: z.enum(VISIT_ROLES).optional(),
    startsOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullish(),
    endsOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullish(),
    durationDays: z.number().int().positive().max(365).nullish(),
    targetOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullish(),
    billingPlanId: z.string().uuid().nullish(),
    optional: z.boolean().optional(),
    chooseGroupId: z.string().uuid().nullish(),
    chooseGroupName: z.string().trim().min(1).max(120).optional(),
    /*
     * **Part of plans** (docs/proposal.md §10.5). `optionIds` is the *whole*
     * set the editor holds, never a delta: a partial update would mean the
     * builder had to send a remove and an add and could leave a visit in a plan
     * nobody is looking at. `newOptions` are the ones typed into the same
     * control and created in the same request, because "Create plan Bronze" on
     * Enter and then ticking it is one decision, and two requests can half-fail.
     */
    optionIds: z.array(z.string().uuid()).max(20).optional(),
    /*
     * `groupId` says which question a new choice joins when a quote has two;
     * left out, the server uses the visit's own, the only one, or makes the
     * first — named `DEFAULT_QUESTION_NAME` (quote-build-refresh §8.4).
     */
    newOptions: z.array(z.object({
        name: z.string().trim().min(1).max(80),
        groupId: z.string().uuid().optional(),
    })).max(10).optional(),
    noteQuote: z.string().trim().max(4000).optional(),
    noteInternal: z.string().trim().max(4000).optional(),
    noteCrew: z.string().trim().max(4000).optional(),
    /*
     * **Add a visit after this one** (visits §5.1.4). On create only: the new
     * visit lands directly below `afterVisitId`, and with `follow` it starts
     * *after that visit is done* — a relation, so it moves when its anchor does.
     */
    afterVisitId: z.string().uuid().optional(),
    follow: z.boolean().optional(),
    /*
     * **On the proposal** (`docs/proposal.md` §10.5, migration `0114`).
     *
     * Each one is "same as the quote" when null, which is why they are
     * `nullish` rather than `optional`: `undefined` means the popover did not
     * touch this field and `null` means somebody chose to go back to the
     * quote's answer, and a schema that could not tell those apart would make
     * "same as the quote" a thing you can set but never unset.
     */
    proposalTiming: z.enum(TIMING_MODES).nullish(),
    proposalPriceView: z.enum(PRICE_VIEWS).nullish(),
    proposalDetail: z.enum(PROPOSAL_DETAILS).nullish(),
    /** A phrase from the company's library. */
    proposalPhraseId: z.string().uuid().nullish(),
    /** Or this visit's own words, which win over the phrase it picked. */
    proposalPhraseText: z.string().trim().max(160).nullish(),
});
export const lineRefSchema = z.object({
    itemId: z.string().uuid().nullish(),
    laborRateId: z.string().uuid().nullish(),
    equipmentId: z.string().uuid().nullish(),
    rentalEquipmentId: z.string().uuid().nullish(),
    deploymentCostId: z.string().uuid().nullish(),
});
export const lineCreateSchema = lineRefSchema.extend({
    qty: qty.default(1),
    handling: z.enum(LINE_HANDLING).nullish(),
});
export const serviceInputSchema = z.object({
    name: z.string().trim().max(160).optional(),
    divisionId: z.string().uuid().optional(),
    serviceId: z.string().uuid().nullish(),
    measure: qty.nullish(),
    measurementId: z.string().uuid().nullish(),
    optional: z.boolean().optional(),
    /**
     * Every line it adds is taxed. Set from the catalog service when it is
     * added; the quoter may change it. Never copied onto the lines, so turning
     * it off gives each line back its own setting (`lineTaxableSql`).
     */
    taxable: z.boolean().optional(),
    /**
     * Its three notes (0125), the visit's `noteQuote`/`noteInternal`/`noteCrew`
     * at the same lengths. On create they default to the catalog service's;
     * on a PATCH an absent one is left as it is.
     */
    noteCustomer: z.string().trim().max(4000).optional(),
    noteInternal: z.string().trim().max(4000).optional(),
    noteCrew: z.string().trim().max(4000).optional(),
    /**
     * The line this service exists to hold. Nothing can go on a quote without a
     * service above it, so "add a bag of sand" to a quote with none open has to
     * make one — and the two have to happen together, or a failure leaves an
     * empty service named after something that never arrived.
     */
    line: lineCreateSchema.optional(),
});
/**
 * **Customer-facing name** (migration 0126) — on a catalog record, a recipe
 * line and a quote line alike. The column's CHECK caps it at the longest of
 * the five catalog names (an item's) and refuses a blank.
 */
export const CUSTOMER_NAME_MAX = 120;
/**
 * The box as a whole-record writer takes it: trimmed, and blank is null.
 *
 * Null is "the same as the name", which is what an empty box means to the
 * person looking at it — it shows the name as its placeholder. An empty
 * string would be a name that prints nothing, and the column refuses one.
 */
export const customerNameSchema = z.string().trim().max(CUSTOMER_NAME_MAX).nullish()
    .transform((value) => (value ? value : null));
/**
 * What to store for a customer-facing name typed over `fallback` — the name
 * a blank box prints, which is also its placeholder.
 *
 * Blank is null, and so is the fallback typed out in full: storing a copy of
 * the name would stop following it the day it was written, and "rename it
 * back" is something somebody does by retyping it. The quote line PATCH and
 * both line popovers decide it here, so the box and the server cannot
 * disagree about what Done changed.
 */
export function customerNameToStore(typed, fallback) {
    const value = (typed ?? '').trim();
    return value === '' || value === fallback.trim() ? null : value;
}
export const linePatchSchema = z.object({
    /*
     * Zero is a quantity a line can have — the builder's box takes it, and a
     * package line scaled by a measure nobody has entered already sits at it.
     * Refusing it here left the box reading 0 over a line whose quantity and
     * cost had not moved, which is a cost on the quote nobody can see the
     * reason for.
     */
    qty: z.number().finite().nonnegative().optional(),
    /** Below standard needs a reason; the server refuses without one. */
    rate: money.optional(),
    editReason: z.string().trim().max(400).nullish(),
    handling: z.enum(LINE_HANDLING).nullish(),
    optional: z.boolean().optional(),
    taxable: z.boolean().optional(),
    showOnProposal: z.boolean().optional(),
    /**
     * The name the customer reads on this quote (0126). `nullish` rather than
     * `optional`, because null is a value here: absent leaves it alone, null
     * (or a blank, or the description itself) goes back to the description.
     */
    customerName: z.string().trim().max(CUSTOMER_NAME_MAX).nullish(),
});
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const planInputSchema = z.object({
    name: z.string().trim().min(1).max(120).optional(),
    kind: z.enum(BILLING_PLAN_KINDS).optional(),
    depositPct: z.number().finite().gt(0).lt(100).nullish(),
    termMonths: z.number().int().positive().max(60).nullish(),
    dayOfMonth: z.number().int().min(1).max(28).nullish(),
    dayOfWeek: z.number().int().min(0).max(6).nullish(),
    periodUnit: z.enum(PERIOD_UNITS).optional(),
    periodEvery: z.number().int().positive().max(365).optional(),
    periodCount: z.number().int().positive().max(520).nullish(),
    periodBasis: z.enum(PERIOD_BASES).optional(),
    billFrom: isoDate.nullish(),
    billTo: isoDate.nullish(),
    tmTrigger: z.enum(TM_TRIGGERS).optional(),
    capAmount: money.nullish(),
    termsDays: z.number().int().min(0).max(180).optional(),
    termsMode: z.enum(TERMS_MODES).optional(),
    completionMode: z.enum(COMPLETION_MODES).optional(),
    partPeriod: z.enum(PART_PERIODS).optional(),
    /** Dollars; setting it clears `depositPct`, and setting that clears this. */
    depositFlat: money.refine((value) => value > 0).nullish(),
    restMode: z.enum(REST_MODES).optional(),
    milestones: z.array(z.object({
        name: z.string().trim().min(1).max(120),
        trigger: z.enum(MILESTONE_TRIGGERS),
        dueOn: isoDate.nullish(),
        pct: z.number().finite().gt(0).max(100).nullish(),
        amount: money.nullish(),
        /** Required when the trigger is `progress`; ignored otherwise. */
        progressPct: z.number().finite().gt(0).max(100).nullish(),
        /** The visit a `visit_completed` stage waits for; ignored on any other trigger. */
        visitId: z.string().uuid().nullish(),
    })).max(20).optional(),
});
export const reorderSchema = z.object({
    /** Options, per group: the plans' left-to-right order on the proposal. */
    options: z.record(z.string().uuid(), z.array(z.string().uuid())).optional(),
    visits: z.array(z.string().uuid()).optional(),
    services: z.record(z.string().uuid(), z.array(z.string().uuid())).optional(),
    lines: z.record(z.string().uuid(), z.array(z.string().uuid())).optional(),
});
/**
 * An answer to a choose-one question (docs/proposal.md §7.3).
 *
 * **There is no price here and there never will be.** A plan's price is the
 * sum of the visits that are part of it, so moving a visit moves both totals;
 * a typed figure would be a second answer that goes stale the first time
 * anybody edits a line.
 */
/**
 * What the customer is asked when a quote's first choice is made in the visit
 * editor and nobody has named the question yet (quote-build-refresh §8.4,
 * decision 2). Rewordable from the field's helper line and from the plans
 * card; a package with no proposal text of its own starts from it too.
 */
export const DEFAULT_QUESTION_NAME = 'Your choice';
export const optionInputSchema = z.object({
    groupId: z.string().uuid().optional(),
    /** "+ New question" (qb-05 · 5): the choice starts a question of its own. */
    newQuestion: z.boolean().optional(),
    name: z.string().trim().min(1).max(80).optional(),
    tagline: z.string().trim().max(120).nullish(),
    recommended: z.boolean().optional(),
    imageFileId: z.string().uuid().nullish(),
});
/** `{ name?, allowNone? }` on the question itself (§7.4). */
export const chooseGroupInputSchema = z.object({
    name: z.string().trim().min(1).max(120).optional(),
    allowNone: z.boolean().optional(),
});
export const approveSchema = z.object({
    /**
     * One **option** id per choose-one group, or `null` for "none of these"
     * where the group allows it (§7.4).
     *
     * A *visit* id is accepted for one release and mapped to the single-visit
     * option holding it: the page before PR 4, the office's own Selections
     * panel and every saved payload all speak visit ids, and one validation
     * serves three callers rather than each learning the new shape separately.
     */
    chooseSelections: z.record(z.string().uuid(), z.string().uuid().nullable())
        .default({}),
    /** Which optional visits the customer ticked. */
    optionalAdds: z.array(z.string().uuid()).default([]),
    /**
     * Optional **services** and **lines** switched on (§7.2). They are separate
     * lists rather than one because the rules differ: a service's parent must be
     * an included visit, a line's must be an included service, and an error
     * naming the wrong level is one nobody can act on.
     */
    serviceAdds: z.array(z.string().uuid()).default([]),
    lineAdds: z.array(z.string().uuid()).default([]),
    signatureName: z.string().trim().min(1).max(160).optional(),
    signatureFileId: z.string().uuid().nullish(),
    /** Who signed it, when the approval came from a named contact. */
    approvedByContactId: z.string().uuid().nullish(),
});
// ---------------------------------------------------------------------------
// Two things both sides work out the same way
// ---------------------------------------------------------------------------
/**
 * Whether the schedule is deciding how many visits this is.
 *
 * A recurring schedule with both ends of a season on it answers the question
 * by arithmetic — Weekly from Apr 6 to Oct 26 is thirty mowings and there is
 * nothing to argue about. Anything else leaves it to whoever is quoting:
 * Weekly with no end date is "however many you want", and one-time and custom
 * never had a schedule to count.
 */
export function countIsScheduled(visit) {
    return SCHEDULE_PER_WEEK[visit.schedule] !== null
        && Boolean(visit.startsOn) && Boolean(visit.endsOn);
}
/**
 * A recurring visit that something other than the calendar will end.
 *
 * Note what this is *not*: "weekly, no end date typed yet" is an ordinary
 * visit whose count the quoter types (migration 0065 exists so that can be
 * said at all). Open-ended is narrower — the trigger states that the end is
 * decided elsewhere, by the rental term or by a phone call — and only then is
 * the number of trips genuinely unknowable rather than merely unfilled.
 */
export function isOpenEnded(visit) {
    if (visit.repeatKind === 'every' && visit.endsKind === 'never')
        return true;
    return SCHEDULE_PER_WEEK[visit.schedule] !== null
        && visit.endsOn === null
        && visit.trigger !== undefined && visit.trigger !== 'date';
}
/**
 * How many trips a visit is.
 *
 * **The schedule wins when it can answer.** A season and a frequency are a
 * count, and the count has to follow when either changes — Weekly to
 * Bi-weekly halves the work and has to halve the number beside it, or the
 * quote says thirty mowings and prices fifteen.
 *
 * `qty` is what somebody typed into the × N box, and it decides the count
 * whenever the dates cannot: no end date, a one-time visit, a custom rule.
 * It used to win outright, which is why changing the frequency changed
 * nothing — the typed thirty was still there, silently outvoting the dates
 * underneath it.
 */
export function occurrencesOf(visit) {
    // Nothing on the quote knows how many, and nothing should pretend to.
    if (isOpenEnded(visit))
        return Number.POSITIVE_INFINITY;
    const typed = Math.max(1, Math.round(visit.qty) || 1);
    const perWeek = SCHEDULE_PER_WEEK[visit.schedule];
    if (!countIsScheduled(visit) || perWeek === null)
        return typed;
    const weeks = (Date.parse(`${visit.endsOn}T00:00:00Z`)
        - Date.parse(`${visit.startsOn}T00:00:00Z`)) / (7 * 86_400_000);
    if (!Number.isFinite(weeks) || weeks < 0)
        return typed;
    return Math.max(1, Math.round(weeks * perWeek) + 1);
}
/**
 * How many visits an approved quote makes of this one (`docs/visit-page.md`
 * §26.3): one for open-ended work — its series generator adds the rest —
 * else the trips `occurrencesOf()` counts, never fewer than one.
 *
 * **One number, three readers**: the schedule line and the proposal's count
 * print it (`scheduleLabel`, `timingOf`, the proposal's badge), the approval
 * writes that many visits (`writeQuoteVisits`), and the approval's
 * expectations store it for the database to count against at COMMIT. A
 * second way of working it out is how a customer is shown thirty and given
 * twenty-nine.
 */
export function expectedCountFor(visit) {
    const openEnded = visit.openEnded ?? !Number.isFinite(visit.occurrences);
    if (openEnded || !Number.isFinite(visit.occurrences))
        return 1;
    return Math.max(1, Math.round(visit.occurrences) || 1);
}
/**
 * What a customer may add *inside* a visit: its optional services, and the
 * optional lines of the services it is buying anyway.
 *
 * The builder's rail lists these under the visit they join, so "If all
 * options taken" is a sum of rows somebody can see rather than a figure with
 * an optional bag of fertilizer hiding in it. The same terms `recalcQuote`
 * adds to `options_total`: once per trip, nothing for a declined visit, and
 * nothing for open-ended work, whose money is an amount per period.
 */
export function visitExtras(visit) {
    if (visit.declined || visit.openEnded)
        return [];
    const trips = visit.occurrences;
    const out = [];
    for (const service of visit.services) {
        if (service.optional) {
            out.push({ id: service.id, name: service.name, amount: service.total * trips });
            continue;
        }
        for (const line of service.lines) {
            if (line.optional) {
                out.push({ id: line.id, name: line.description, amount: line.amount * trips });
            }
        }
    }
    return out;
}
/**
 * The date of each occurrence, as far as the schedule knows them.
 *
 * Approval turns a visit into one work item per occurrence, and a work item
 * with no date is a work item the scheduler has to guess at. A weekly visit
 * from Apr 6 knows all thirty of its dates; a one-time visit knows its target
 * or nothing, and `custom` knows nothing by construction — those get `null`
 * rather than a date somebody invented, because the office booking it is the
 * one who decides.
 */
export function occurrenceDates(visit) {
    // An open-ended visit has no list of dates; approval arms a generator for it
    // rather than creating work items nobody can count (rentals-implementation
    // §4). Returning an empty list is what says "not this way".
    if (!Number.isFinite(visit.occurrences))
        return [];
    const count = Math.max(1, visit.occurrences);
    const step = { weekly: 7, biweekly: 14, monthly: 0, one_time: 0, custom: 0 }[visit.schedule];
    if (visit.schedule === 'one_time' || visit.schedule === 'custom' || !visit.startsOn) {
        return Array.from({ length: count }, () => visit.targetOn ?? visit.startsOn ?? null);
    }
    const start = Date.parse(`${visit.startsOn}T00:00:00Z`);
    return Array.from({ length: count }, (_unused, index) => {
        if (visit.schedule === 'monthly') {
            const date = new Date(start);
            date.setUTCMonth(date.getUTCMonth() + index);
            return date.toISOString().slice(0, 10);
        }
        return new Date(start + index * step * 86_400_000).toISOString().slice(0, 10);
    });
}
/**
 * The line under a visit's name: "Weekly · Apr 6 – Oct 26 · 30 visits".
 *
 * Built in one place because it prints in four — the visit card, the phone
 * card, the billing tab's assignment row and the proposal — and four
 * renderings of one sentence is four chances to describe the same schedule
 * differently.
 */
/**
 * What an unnamed visit is called: everything on it, in order.
 *
 * A visit is a trip to the property, and what it is *for* is the work on it —
 * so a visit nobody has named reads as its services. One service is its name;
 * several are a list, with an ampersand before the last, because "Mowing,
 * Edging and Blowing" is a sentence and a visit card is a label.
 *
 * Named after the first service only, which is what this replaced, a visit
 * holding three services claimed to be one of them.
 */
export function visitName(serviceNames) {
    const named = serviceNames.map((name) => name.trim()).filter(Boolean);
    if (named.length === 0)
        return 'New visit';
    if (named.length === 1)
        return named[0];
    return `${named.slice(0, -1).join(', ')} & ${named.at(-1)}`;
}
export function scheduleLabel(visit) {
    const short = (iso) => (iso
        ? new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
        : null);
    /*
     * There is no custom rule (visits contract §13). A visit scheduled before
     * 0121 with one is labelled by the server, which still holds the text.
     */
    if (visit.schedule === 'custom')
        return VISIT_SCHEDULE_LABELS.custom;
    if (visit.schedule === 'one_time') {
        // A trip with no date because nobody has asked for it yet.
        if (visit.trigger === 'on_call')
            return VISIT_TRIGGER_LABELS.on_call;
        const parts = ['One-time'];
        if (visit.durationDays) {
            parts.push(`${visit.durationDays} ${visit.durationDays === 1 ? 'day' : 'days'}`);
        }
        if (visit.targetOn) {
            parts.push(`target ${new Date(`${visit.targetOn}T00:00:00Z`)
                .toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' })}`);
        }
        return parts.join(' · ');
    }
    const parts = [VISIT_SCHEDULE_LABELS[visit.schedule]];
    const from = short(visit.startsOn);
    const to = short(visit.endsOn);
    if (from && to)
        parts.push(`${from} – ${to}`);
    // "Weekly · from Oct 6 · while on rent" — a count here would be a fiction.
    if (isOpenEnded(visit)) {
        if (from && !to)
            parts.push(`from ${from}`);
        parts.push(VISIT_TRIGGER_LABELS[visit.trigger ?? 'term_end'].toLowerCase());
        return parts.join(' · ');
    }
    const count = expectedCountFor({ occurrences: visit.occurrences, openEnded: false });
    parts.push(`${count} ${count === 1 ? 'visit' : 'visits'}`);
    return parts.join(' · ');
}
//# sourceMappingURL=quote-v2.js.map